<script>
import { mask } from 'vue-the-mask'
import { masks } from '@constants/Masks'
import moment from 'moment'
import { aawzDate } from '@utils/dates/aawz-date'
import uploadFile from '@modules/global/components/upload-file-v2.vue'
import { MASK_BRL, MASK_USD } from '@modules/global/constants/Currencies'
import QuarterPicker from '@components/quarter-picker.vue'
import SelectSearch from '@modules/global/components/select-search/select-search.vue'
import MarkdownField from '@modules/global/components/markdown-field/markdown-field.vue'
import ClockTimePicker from '@components/clock-time-picker/clock-time-picker.vue'
import { isFieldRequired, stripRequiredMark } from '@utils/requiredField'

// Padding-top do labelExternal quando labelPosition="left", pra alinhar
// verticalmente com o texto do input em vez da borda do campo.
const LABEL_LEFT_PADDING_TOP = '12px'

export default {
  directives: {
    mask,
  },
  components: {
    QuarterPicker,
    uploadFile,
    SelectSearch,
    MarkdownField,
    ClockTimePicker,
  },
  props: {
    value: {
      type: [String, Number, Object, File, Array, Boolean],
      default: null,
    },
    idInput: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
      validator: (val) =>
        [
          'text',
          'textarea',
          'textarea-new-ux',
          'str',
          'number',
          'decimal', // Preenchimento da direita para esquerda
          'decimal_reverse', // Preenchimento da esquerda para direita
          'percent',
          'money',
          'money_dolar',
          'integer',
          'float',
          'date',
          'date_ref',
          'date_new_ux',
          /**
           * Campo principalmente para filtros, que por padrão utilizamos o
           * ultido dia do mês. Dessa forma, o campo apresenta para o usuario
           * somente o mês e ano (DD/YYYY), porém os dados ficam no formato
           * completo (DD/MM/YYYY).
           */
          'date_month',
          'date_month_new_ux', // Usando novo design
          'date_quarter',
          'date_with_widget',
          'cnpj',
          'cpf',
          'cpf_cnpj',
          'cep',
          'telefone',
          'phoneWithCountryCodeBrazil',
          'email',
          'e-mail',
          'file',
          'search',
          'codigo_boleto',
          'select',
          'select-search',
          'select-search-props',
          'year',
          'time',
          'clock_time',
          'liquidity',
          'checkbox',
          'markdown-editor',
          'autocomplete',
        ].includes(val),
    },
    options: {
      type: Array,
      default: () => [],
    },
    colorIcon: {
      type: String,
      default: () => '#8e8e8e',
    },
    disabled: Boolean,
    // loading: {
    //   type: Boolean,
    // },
    // Esconder os valores do componente
    hideData: Boolean,
    minDate: {
      type: String,
      default: '',
    },
    maxDate: {
      type: String,
      default: '',
    },
    maxWidth: {
      type: String,
      default: '250px',
    },
    startPickerOnYear: {
      type: Boolean,
      default: false,
    },
    // Direção em que o calendário abre. O padrão do componente é alinhar o
    // menu pela direita do campo (`left` do Vuetify) — certo quando o input
    // fica no fim de uma toolbar. Num campo colado na borda esquerda do
    // conteúdo esse alinhamento empurra os 290px do picker para fora da área
    // útil e o menu lateral cobre a primeira coluna de meses; nesses casos
    // abra o menu para a direita.
    openMenuRight: {
      type: Boolean,
      default: false,
    },
    labelExternal: {
      type: String,
      default: null,
    },
    // Contador de caracteres do textarea (ex: 4000 do NC-6, spec-002). Repassa
    // para o `counter` nativo do Vuetify (v-textarea) — false desliga o contador.
    counter: {
      type: [Boolean, Number, String],
      default: false,
    },
    // Marca o campo como obrigatório: exibe asterisco vermelho no labelExternal.
    // Também é detectado automaticamente quando o labelExternal termina em "*".
    required: {
      type: Boolean,
      default: false,
    },
    // Posição do labelExternal: 'top' (default, rótulo acima do campo) ou
    // 'left' (rótulo à esquerda, largura fixa via labelWidth + campo ocupando
    // o restante). Checkbox não usa essa div (v-checkbox já tem :label
    // inline). Em labelPosition="left" o template reserva um espaçador vazio
    // do mesmo width tanto pro checkbox quanto pra campo sem labelExternal,
    // pra manter a coluna alinhada com os campos vizinhos que têm label.
    labelPosition: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'left'].includes(value),
    },
    // Largura do labelExternal quando labelPosition="left".
    labelWidth: {
      type: String,
      default: '20%',
    },
  },
  data() {
    return {
      masks,
      WIDTH_FILTER_FIELDS: 175,
      showMenu: false,
      MASK_BRL,
      MASK_USD,
      LABEL_LEFT_PADDING_TOP,
    }
  },
  computed: {
    // labelExternal sem o "*" legado (o asterisco vira elemento vermelho próprio).
    labelExternalText() {
      return stripRequiredMark(this.labelExternal)
    },
    // Asterisco vermelho quando o campo é obrigatório (rules.required, prop ou "*").
    showRequiredAsterisk() {
      return isFieldRequired({
        rules: this.$attrs.rules,
        required: this.required,
        label: this.labelExternal,
      })
    },
    dateMonth: {
      get() {
        if (typeof this.value !== 'string' || !this.value) return ''
        // Strict parse: a partial mask value (e.g. "01/01/1" while the user is
        // still typing the year) must NOT be accepted as a valid date with a
        // 1-digit year (Sentry AAWZ-WEB-3YE).
        const d = moment(this.value, 'DD/MM/YYYY', true)
        if (d.isValid()) return d.format('YYYY-MM')
        const dIso = moment(this.value, 'YYYY-MM-DD', true)
        if (dIso.isValid()) return dIso.format('YYYY-MM')
        return ''
      },
      set(newValue) {
        const val = moment(newValue, 'YYYY-MM')
          .endOf('month')
          .format('DD/MM/YYYY')
        this.updateVal(val)
      },
    },
    dateDay: {
      get() {
        if (typeof this.value !== 'string' || !this.value) return ''
        // Strict parse: a partial mask value (e.g. "01/01/1" while the user is
        // still typing the year) must NOT be accepted as a valid date with a
        // 1-digit year. Non-strict moment parsed it as year 0001, which reached
        // Vuetify's VDatePickerYears and crashed Intl.DateTimeFormat with
        // "RangeError: Invalid time value" (Sentry AAWZ-WEB-3YE).
        const d = moment(this.value, 'DD/MM/YYYY', true)
        if (d.isValid()) return d.format('YYYY-MM-DD')
        const dIso = moment(this.value, 'YYYY-MM-DD', true)
        if (dIso.isValid()) return dIso.format('YYYY-MM-DD')
        return ''
      },
      set(newValue) {
        const val = moment(newValue, 'YYYY-MM-DD').format('DD/MM/YYYY')
        this.updateVal(val)
      },
    },
    dateYearFirstDisplay: {
      get() {
        if (typeof this.value !== 'string' || !this.value) return ''

        const parsed = moment(
          this.value,
          ['DD/MM/YYYY', 'YYYY/MM/DD', 'YYYY-MM-DD'],
          true
        )

        if (parsed.isValid()) {
          return parsed.format('YYYY/MM/DD')
        }

        return this.value
      },
      set(newValue = '') {
        if (!newValue) {
          this.updateVal('')
          return
        }

        const sanitized = String(newValue).replace(/-/g, '/')

        if (sanitized.length === 10) {
          const parsed = moment(sanitized, 'YYYY/MM/DD', true)

          if (parsed.isValid()) {
            this.updateVal(parsed.format('DD/MM/YYYY'))
            return
          }
        }

        this.updateVal(sanitized)
      },
    },

    formatDateMin() {
      // Returns undefined (not '') so VDatePicker treats min as unset.
      // Ref Sentry AAWZ-WEB-3NE / AAWZ-WEB-3NF.
      if (typeof this.minDate === 'string' && this.minDate.length >= 10) {
        const parsed = moment(this.minDate, 'DD/MM/YYYY')
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD')
      }
      return undefined
    },
    formatDateMax() {
      // Returns undefined (not '') so VDatePicker treats max as unset.
      // Ref Sentry AAWZ-WEB-3NE / AAWZ-WEB-3NF.
      if (typeof this.maxDate === 'string' && this.maxDate.length >= 10) {
        const parsed = moment(this.maxDate, 'DD/MM/YYYY')
        if (parsed.isValid()) return parsed.format('YYYY-MM-DD')
      }
      if (this.startPickerOnYear) {
        return moment().format('YYYY-MM-DD')
      }
      return undefined
    },
  },
  watch: {
    showMenu(val) {
      if (val && this.startPickerOnYear && this.type === 'date_new_ux') {
        this.$nextTick(() => {
          this.focusYearPicker()
        })
      }
    },
  },
  methods: {
    handleRootClick(evt) {
      if (this.disabled) return
      this.$emit('click', evt)
    },
    // Repassa pro SelectSearch interno (quando type é select-search[-props]) —
    // no-op silencioso pros demais tipos. Usado por quem segura um $ref deste
    // componente pra fechar/commitar um dropdown ainda aberto antes de ler o
    // valor (ex: ConsolidatorWalletFilter.blurOpenFields()).
    blur() {
      if (this.$refs.selectSearch) this.$refs.selectSearch.blur()
    },
    updateVal(val) {
      if (val !== this.value) {
        this.$emit('input', val)
        if (this.type === 'file') {
          this.$emit('change', val)
        }
      }
    },
    importFile(file) {
      if (file) {
        this.$emit('add-file', file)
      }
    },
    changeDate(val, ref) {
      if (this.$refs[ref]) {
        this.$refs[ref].save(val)
      }
    },
    /**
     *
     * @param {String} val DD/MM/YYYY
     * @returns {String} MM/YYYY
     */
    formatMonthDate(val) {
      if (val && typeof val === 'string') {
        return val.split('/').slice(1).join('/')
      }
      return val
    },
    /**
     * Formatar a data para um formato de trimestre
     */
    formatQuarterDate(val) {
      if (val && typeof val === 'string') {
        const [, month, year] = val.split('/')
        return `${Math.floor(Number(month) / 4) + 1}T/${year}`
      }
      return val
    },
    openFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.$refs.input.click()
      }
    },
    formatDate(val) {
      const d = aawzDate(val, 'YYYY-MM-DD')
      if (d.isValid()) {
        this.updateVal(d.format('DD/MM/YYYY'))
        this.showMenu = false
        this.$emit('change')
      }
      return val
    },
    unformartDate(val) {
      if (val) {
        const d = aawzDate(val, 'DD/MM/YYYY')
        if (d.isValid()) {
          return d.format('YYYY-MM-DD')
        }
      }
      // Return null (not val) so VDatePicker never receives an invalid date string.
      // Returning a non-null invalid value causes Vuetify's makeIsoString to produce
      // strings like "0025-NaN", which crash Intl.DateTimeFormat (Sentry AAWZ-WEB-3PY).
      return null
    },
    focusYearPicker() {
      const picker = this.$refs.pickerDateYearFirst

      if (!picker) {
        return
      }

      picker.activePicker = 'YEAR'

      const currentYear = aawzDate().format('YYYY')
      const [, monthFromPicker = '01'] = String(picker.tableDate || '').split(
        '-'
      )
      const currentMonth = monthFromPicker.padStart(2, '0')

      picker.tableDate = `${currentYear}-${currentMonth}`
    },
  },
}
</script>

<template>
  <div
    class="input-by-type"
    :class="{ 'd-flex align-start': labelPosition === 'left' }"
    @click="handleRootClick"
  >
    <div
      v-if="labelPosition === 'left' && (type === 'checkbox' || !labelExternal)"
      class="az-text-label"
      :style="{ width: labelWidth, flexShrink: 0 }"
    />
    <div
      v-else-if="labelExternal && type !== 'checkbox'"
      class="az-text-label"
      :style="
        labelPosition === 'left'
          ? {
              width: labelWidth,
              flexShrink: 0,
              paddingTop: LABEL_LEFT_PADDING_TOP,
            }
          : {}
      "
    >
      {{ labelExternalText }}
      <span
        v-if="showRequiredAsterisk"
        class="az-required-asterisk"
        aria-hidden="true"
      >
        *
      </span>
    </div>
    <div
      class="input-by-type__field"
      :class="{ 'flex-grow-1': labelPosition === 'left' }"
    >
      <div v-if="type === 'file'" style="position: relative">
        <uploadFile
          :value="value"
          :id-input="idInput || 'upload-file-input'"
          default-text=" "
          :disabled="disabled"
          v-bind="$attrs"
          :label="label"
          v-on="$listeners"
          @change="updateVal"
          @input="importFile"
        >
        </uploadFile>
      </div>
      <v-text-field
        v-else-if="type === 'date'"
        v-mask="masks.data"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:prepend-inner>
          <icon-svg name="calendar" size="18" :style="{ color: colorIcon }" />
        </template>
      </v-text-field>
      <v-menu
        v-else-if="type === 'date_new_ux'"
        ref="menuDateUX"
        v-model="showMenu"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="290"
        min-width="auto"
        min-height="auto"
        :left="!openMenuRight"
        offset-y
        class="remove-date-margin"
        v-bind="$attrs"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            v-mask="masks.data"
            :value="value"
            :label="label"
            placeholder="DD/MM/AAAA"
            hide-details
            dense
            class="new-ux align-icon-search"
            outlined
            :disabled="disabled"
            v-bind="attrs"
            v-on="on"
            @input="updateVal"
          >
            <template v-slot:prepend-inner>
              <icon-svg name="calendar" size="20" style="color: #8e8e8e" />
            </template>
          </v-text-field>
        </template>
        <v-date-picker
          ref="pickerDateYearFirst"
          v-model="dateDay"
          type="date"
          no-title
          scrollable
          locale="pt-br"
          :min="formatDateMin"
          :max="formatDateMax"
          @change="changeDate($event, 'menuDateUX')"
        >
        </v-date-picker>
      </v-menu>
      <v-menu
        v-else-if="type === 'date_month_new_ux'"
        ref="menuDateMonth"
        v-model="showMenu"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="290"
        min-width="auto"
        min-height="auto"
        :left="!openMenuRight"
        offset-y
        v-bind="$attrs"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatMonthDate(value)"
            :label="label"
            placeholder="MM/AAAA"
            readonly
            hide-details
            dense
            class="new-ux align-icon-search"
            outlined
            :disabled="disabled"
            v-bind="attrs"
            v-on="on"
          >
            <template v-slot:prepend-inner>
              <icon-svg
                name="calendar"
                size="20"
                :style="{ color: colorIcon }"
              />
            </template>
          </v-text-field>
        </template>
        <v-date-picker
          v-model="dateMonth"
          type="month"
          no-title
          scrollable
          locale="pt-br"
          :min="formatDateMin"
          :max="formatDateMax"
          @change="changeDate($event, 'menuDateMonth')"
        >
        </v-date-picker>
      </v-menu>
      <v-menu
        v-else-if="type === 'date_quarter'"
        ref="menuDateQuarter"
        v-model="showMenu"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="290"
        min-width="auto"
        min-height="auto"
        :left="!openMenuRight"
        offset-y
        v-bind="$attrs"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatQuarterDate(value)"
            :label="label"
            placeholder="MM/AAAA"
            readonly
            hide-details
            dense
            class="new-ux align-icon-search"
            outlined
            :disabled="disabled"
            v-bind="attrs"
            v-on="on"
          >
            <template v-slot:prepend-inner>
              <icon-svg name="calendar" size="20" style="color: #8e8e8e" />
            </template>
          </v-text-field>
        </template>
        <QuarterPicker
          v-model="dateMonth"
          @change="changeDate($event, 'menuDateQuarter')"
        />
      </v-menu>
      <v-menu
        v-else-if="type === 'date_month'"
        ref="menuDateMonth"
        v-model="showMenu"
        :close-on-content-click="false"
        transition="scroll-y-transition"
        :max-width="290"
        min-width="auto"
        min-height="auto"
        :left="!openMenuRight"
        offset-y
        v-bind="$attrs"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="formatMonthDate(value)"
            :label="label"
            placeholder="MM/AAAA"
            readonly
            :style="{ 'max-width': '100px' }"
            hide-details
            dense
            class="no-border"
            :disabled="disabled"
            v-bind="attrs"
            v-on="on"
          >
            <template v-slot:prepend-inner>
              <icon-svg
                name="calendar"
                size="20"
                :style="{ color: colorIcon }"
              />
            </template>
          </v-text-field>
        </template>
        <v-date-picker
          v-model="dateMonth"
          type="month"
          no-title
          scrollable
          locale="pt-br"
          :min="formatDateMin"
          :max="formatDateMax"
          @change="changeDate($event, 'menuDateMonth')"
        >
        </v-date-picker>
      </v-menu>
      <v-text-field
        v-else-if="type === 'date_with_widget'"
        v-mask="masks.data"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        class="input-date-with-widget"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:prepend-inner>
          <v-menu
            ref="menu1"
            v-model="showMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            :disabled="$attrs.readonly"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <icon-svg
                  name="calendar"
                  size="20"
                  style="color: var(--primary)"
                />
              </v-btn>
            </template>
            <v-date-picker
              :value="unformartDate(value)"
              no-title
              locale="pt-br"
              @input="formatDate($event)"
            />
          </v-menu>
        </template>
      </v-text-field>
      <v-text-field
        v-else-if="type === 'date_ref'"
        v-mask="masks.dataRef"
        placeholder="MM/YYYY"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-currency-field
        v-else-if="type === 'money'"
        :value="value"
        :label="label"
        :prefix="`${MASK_BRL} `"
        autocomplete="off"
        :type="hideData ? 'password' : 'text'"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:append>
          <slot name="append"></slot>
        </template>
      </v-currency-field>
      <v-currency-field
        v-else-if="type === 'money_dolar'"
        :value="value"
        :label="label"
        :prefix="`${MASK_USD} `"
        autocomplete="off"
        :type="hideData ? 'password' : 'text'"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:append>
          <slot name="append"></slot>
        </template>
      </v-currency-field>
      <v-currency-field
        v-else-if="type === 'percent'"
        :value="value"
        :label="label"
        suffix=" %"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:append>
          <slot name="append"></slot>
        </template>
      </v-currency-field>
      <v-currency-field
        v-else-if="['decimal', 'float'].includes(type)"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-currency-field
        v-else-if="'decimal_reverse' === type"
        :value="value"
        :label="label"
        autocomplete="off"
        :allow-negative="false"
        :auto-decimal-mode="false"
        :decimal-length="{ min: 0, max: 15 }"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-currency-field
        v-else-if="['number', 'integer'].includes(type)"
        :value="value"
        :label="label"
        :decimal-length="0"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:append>
          <slot name="append"></slot>
        </template>
      </v-currency-field>
      <v-text-field
        v-else-if="type === 'cnpj'"
        v-mask="masks.cnpj"
        placeholder="00.000.000/0000-00"
        :value="value"
        :label="label"
        :type="hideData ? 'password' : 'text'"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'cpf'"
        v-mask="masks.cpf"
        placeholder="000.000.000-00"
        :value="value"
        :label="label"
        :type="hideData ? 'password' : 'text'"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'cpf_cnpj'"
        v-mask="[masks.cpf, masks.cnpj]"
        placeholder="000.000.000-00"
        :value="value"
        :label="label"
        :type="hideData ? 'password' : 'text'"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'cep'"
        v-mask="masks.cep"
        placeholder="00000-000"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'telefone'"
        v-mask="[masks.phoneShort, masks.phoneLong]"
        placeholder="(00) 0000-0000"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'phoneWithCountryCodeBrazil'"
        v-mask="[
          masks.phoneWithCountryCodeBrazil,
          masks.phoneWithCountryCodeBrazilExtended,
        ]"
        placeholder="+55 (00) 0000-0000"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="['e-mail', 'email'].includes(type)"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="'search' === type"
        :placeholder="$attrs.placeholder || 'Pesquisar'"
        filled
        class="no-border align-icon-search icon-primary"
        hide-details
        dense
        style="overflow: hidden"
        :style="{ maxWidth }"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:prepend-inner>
          <icon-svg name="mdi-magnify" color="var(--neutral-400)" size="20" />
        </template>
      </v-text-field>
      <v-textarea
        v-else-if="'textarea' === type"
        :value="value"
        :label="label"
        :counter="counter"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-textarea
        v-else-if="'textarea-new-ux' === type"
        class="textarea-new-ux"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'codigo_boleto'"
        v-mask="[masks.codigoBoleto, masks.imposto]"
        placeholder="00000.00000 00000.000000 00000.000000 0 00000000000000"
        :value="value"
        :label="label"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-autocomplete
        v-else-if="type === 'select'"
        :value="value"
        :label="label"
        :items="options"
        :disabled="disabled"
        :menu-props="{ offsetY: true }"
        outlined
        class="new-ux"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <SelectSearch
        v-else-if="type === 'select-search'"
        ref="selectSearch"
        :value="value"
        :label="label"
        :items="options"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-if="$scopedSlots.footer" v-slot:footer>
          <slot name="footer" />
        </template>
      </SelectSearch>
      <SelectSearch
        v-else-if="type === 'select-search-props'"
        ref="selectSearch"
        :value="value"
        :label="label"
        :items="options"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:item="slotProps">
          <slot name="item" v-bind="slotProps" />
        </template>
        <template v-slot:selection="slotProps">
          <slot name="selection" v-bind="slotProps" />
        </template>
      </SelectSearch>
      <v-text-field
        v-else-if="type === 'year'"
        v-mask="masks.year"
        :value="value"
        :label="label"
        type="text"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <!-- `@change` (não `@input`): o mixin `selectable` do Vuetify 2 declara
    `model: { prop: 'inputValue', event: 'change' }`, então o v-checkbox NUNCA
    emite `input` (mesmo padrão documentado em ligacoes-list.vue). Escutar
    @input aqui faz `updateVal` nunca disparar num clique real — o checkbox
    marca na tela mas o v-model do consumidor não recebe nada, nos dois
    sentidos (marcar false→true e desmarcar true→false). -->

      <v-checkbox
        v-else-if="type === 'checkbox'"
        class="new-ux input-by-type__checkbox ma-0 pa-0"
        :label="label || labelExternal"
        :input-value="Boolean(value)"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @change="updateVal"
      />
      <v-text-field
        v-else-if="type === 'time'"
        :value="value"
        :label="label"
        type="time"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <ClockTimePicker
        v-else-if="type === 'clock_time'"
        :value="value"
        :label="label"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <v-text-field
        v-else-if="type === 'liquidity'"
        v-mask="masks.liquidity"
        :value="value"
        :label="label"
        placeholder="D+0"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
      <MarkdownField
        v-else-if="type === 'markdown-editor'"
        :value="value"
        :label="label"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-slot:footer>
          <slot name="footer"></slot>
        </template>
      </MarkdownField>
      <v-autocomplete
        v-else-if="type === 'autocomplete'"
        :value="value"
        :label="label"
        :items="options"
        :disabled="disabled"
        autocomplete="off"
        outlined
        :menu-props="{ offsetY: true, ...$attrs['menu-props'] }"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      >
        <template v-if="$scopedSlots.item" v-slot:item="slotProps">
          <slot name="item" v-bind="slotProps" />
        </template>
        <template v-if="$scopedSlots.selection" v-slot:selection="slotProps">
          <slot name="selection" v-bind="slotProps" />
        </template>
      </v-autocomplete>
      <v-text-field
        v-else
        :value="value"
        :label="label"
        :type="hideData ? 'password' : 'text'"
        autocomplete="off"
        :disabled="disabled"
        v-bind="$attrs"
        v-on="$listeners"
        @input="updateVal"
      />
    </div>
  </div>
</template>

<style lang="sass">
.input-by-type
  &__checkbox
    margin-top: 0px !important

.input-date-with-widget .v-input__prepend-inner
  margin-top: 8px !important

.remove-date-margin.v-menu.v-menu--inline
  display: contents !important

.textarea-new-ux
  background: var(--white)
  overflow-y: auto
  width: 100%
  border-radius: 5px
  padding: 10px

  .v-input__slot::before,
  .v-input__slot::after
    display: none !important
</style>
